import {useParams} from "react-router-dom";
import {useSnackbar} from "../../context/SnackbarContext/SnackbarContext";
import React, {useEffect, useState} from "react";
import {
    getNewPatientsCountService, getPatientsCountService, getPatientsWhoPurchasedCountService,
    getSalesByDoctorService,
    getSalesByLocationService, getSalesByProductService,
    getSalesOverTimeService
} from "../../services/dashboardService";
import moment from "moment";
import {useSelector} from "react-redux";

const useDashboard = () => {

    const { showSnackbar } = useSnackbar();

    const authState = useSelector((state) => state.authReducer);

    const [isLoading, setIsLoading] = useState(true);

    const endDate = moment().format('YYYY-MM-DD');
    const startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');

    const [dateRange, setDateRange] = useState([startDate, endDate]);
    const [displayRange, setDisplayRange] = useState('1 Nov, 2024 - 20 Dec, 2024');

    const [salesByProduct, setSalesByProduct] = useState(null);
    const [feesOverTime, setFeesOverTime] = useState(null);
    const [salesCountOverTime, setSalesCountOverTime] = useState(null);
    const [newPatientsCount, setNewPatientsCount] = useState(null);
    const [patientsCount, setPatientsCount] = useState(null);
    const [patientsWhoPurchasedCount, setPatientsWhoPurchasedCount] = useState(null);

    useEffect(() => {
        init()
    }, [dateRange]);

    const init = async () => {
        setIsLoading(true);
        await loadSalesByProduct();
        await loadSalesOverTime();
        await loadNewPatientsCount();
        await loadPatientsCount();
        await loadPatientsWhoPurchasedCount();
        setIsLoading(false);
    };



    const loadSalesByProduct = async () => {

        try {

            const response = await getSalesByProductService({
                start_date: dateRange[0],
                end_date: dateRange[1],
                doctor_id: authState.userId
            });

            const labels = response.data.map(item => item.name);
            const data = response.data.map(item => parseFloat(item.quantity_sold));

            setSalesByProduct({
                labels: labels,
                datasets: [
                    {
                        label: 'Cantidad',
                        data: data,
                        color: 'secondary'
                    }
                ]
            });

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando las ventas por producto', 'cancel', 'warning');
        }

    };

    const loadSalesOverTime = async () => {

        try {

            const response = await getSalesOverTimeService({
                start_date: dateRange[0],
                end_date: dateRange[1],
                doctor_id: authState.userId
            });

            const data = response.data;

            setupSalesCountChart(data, findTimescale(dateRange[0], dateRange[1]));
            setupFeesTotalChart(data, findTimescale(dateRange[0], dateRange[1]));

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando las comisiones', 'cancel', 'warning');
        }

    };

    const loadNewPatientsCount = async () => {

        try {

            const response = await getNewPatientsCountService({
                start_date: dateRange[0],
                end_date: dateRange[1],
                doctor_id: authState.userId
            });

            setNewPatientsCount(response.data);

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando la cantidad de nuevos pacientes', 'cancel', 'warning');
        }

    };

    const loadPatientsCount = async () => {

        try {

            const response = await getPatientsCountService({
                doctor_id: authState.userId
            });

            setPatientsCount(response.data);

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando la cantidad de pacientes', 'cancel', 'warning');
        }

    };

    const loadPatientsWhoPurchasedCount = async () => {

        try {

            const response = await getPatientsWhoPurchasedCountService({
                start_date: dateRange[0],
                end_date: dateRange[1],
                doctor_id: authState.userId
            });

            setPatientsWhoPurchasedCount(response.data);

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando la cantidad de nuevos pacientes', 'cancel', 'warning');
        }

    };

    const setupFeesTotalChart = (data, timescale) => {

        const formatMap = {
            'day': 'DD MMM YYYY',
            'week': 'W Y',
            'month': 'MMM YYYY',
            'year': 'YYYY'
        };

        const unitsCount = moment(dateRange[1]).diff(dateRange[0], timescale);

        const formatString = formatMap[timescale];

        const periods = {};

        for (let i = 0; i <= unitsCount; i++) {
            const key = moment(dateRange[0]).add(i, timescale).format(formatString);
            periods[key] = 0;
        }

        let total = 0;

        data.forEach(item => {
            let periodKey;

            if(timescale === 'week') {
                periodKey = moment(item.period, 'GGGG-WW').format(formatString);
            } else {
                periodKey = moment(item.period).format(formatString);
            }

            if (periods.hasOwnProperty(periodKey)) {
                periods[periodKey] += parseFloat(item.doctor_fee_total);
            }
            total += parseFloat(item.doctor_fee_total);
        });

        setFeesOverTime({
            total,
            labels: Object.keys(periods),
            datasets: [{
                label: 'Comisión',
                data: Object.values(periods),
                color: 'secondary'
            }]
        });
    };

    const setupSalesCountChart = (data, timescale) => {

        const formatMap = {
            'day': 'DD MMM YYYY',
            'week': 'W Y',
            'month': 'MMM YYYY',
            'year': 'YYYY'
        };

        const unitsCount = moment(dateRange[1]).diff(dateRange[0], timescale);

        const formatString = formatMap[timescale];

        const periods = {};

        for (let i = 0; i <= unitsCount; i++) {
            const key = moment(dateRange[0]).add(i, timescale).format(formatString);
            periods[key] = 0;
        }

        let total = 0;

        data.forEach(item => {
            let periodKey;

            if(timescale === 'week') {
                periodKey = moment(item.period, 'GGGG-WW').format(formatString);
            } else {
                periodKey = moment(item.period).format(formatString);
            }

            if (periods.hasOwnProperty(periodKey)) {
                periods[periodKey] += parseFloat(item.sales_count);
            }
            total += parseFloat(item.sales_count);
        });

        setSalesCountOverTime({
            total: total,
            labels: Object.keys(periods),
            datasets: [{
                label: 'Ventas',
                data: Object.values(periods),
                color: 'secondary'
            }]
        });
    };

    const findTimescale = (startDate, endDate) => {
        const diffInDays = moment(endDate).diff(startDate, 'days');

        if (diffInDays <= 14) {
            return 'day';
        } else if (diffInDays <= 90) {
            return 'week';
        } else if (diffInDays <= 365) {
            return 'month';
        } else {
            return 'year';
        }
    };

    return {
        dateRange,
        displayRange,
        setDisplayRange,
        setDateRange,
        isLoading,
        salesByProduct,
        salesCountOverTime,
        feesOverTime,
        patientsWhoPurchasedCount,
        newPatientsCount,
        patientsCount
    };
};

export default useDashboard;
